import { LinkedListWithLayoutSettings } from '@Components/LinkedListWithLayoutSettings';
import { PermissionsList } from '@Components/Permissions';
import { PERMISSIONS_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { groupColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useActivateUser, useBlockUser, usePermissionsWithGroups, useUnblockUser } from '@Hooks/Permissions';
import {
    type ApiGroup,
    type ApiUser,
    EnumAccessState,
    EnumFrontendLayoutSetting,
    EnumPartnershipConsent,
    UserLinkLists,
} from '@Model';
import {
    Button,
    ButtonWithConfirmationModal,
    Card,
    EditFrameMode,
    EditFrameShell,
    EnumMessageLevel,
    EnumMessageType,
    FrameEditBase,
    Grid,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedList,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { FrameEditGroup } from './FrameEditGroup';
import { getPermissionsColumns } from './getPermissionsColumns';
import { getUserStateBadges } from './getUserStateBadges';
import { ResetPasswordButton } from './ResetPasswordButton';

export interface FrameEditUserProps {
    isOwnProfileView?: boolean;
}

export function FrameEditUser(props: IFrameEditShellProps<ApiUser> & FrameEditUserProps) {
    const isOwnProfileView = props.isOwnProfileView;
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = isOwnProfileView ? 'me' : props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    const renderer = useCallback(
        (p: IFrameBodyProps<ApiUser> & FrameEditUserProps) => (
            <FrameEditUserBody isOwnProfileView={isOwnProfileView} {...p} />
        ),
        [isOwnProfileView],
    );

    return (
        <FrameEditBase<ApiUser>
            apiResourcePath={`${PERMISSIONS_API}/users`}
            renderer={renderer}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || isOwnProfileView || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.PermissionsUsersUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.PermissionsUsersCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.PermissionsUsersDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditUserBody(props: IFrameBodyProps<ApiUser> & FrameEditUserProps) {
    const i18n = useTranslate('');
    const hasPermission = useHasPermission();

    const apiUser = props.data;
    const baseApiObjectPath = `${props.apiResourcePath}/${props.data.id}`;

    const isUserActivated = apiUser.is_activated;
    const isUserBlocked = apiUser.access_state === EnumAccessState.Locked;
    // The user was invited but has not confirmed the partnership.
    const isUserPending = apiUser.partnership_consent !== EnumPartnershipConsent.Accepted;
    const isUserRejected = apiUser.partnership_consent === EnumPartnershipConsent.Rejected;

    const stateBadges = useMemo(() => getUserStateBadges(apiUser), [apiUser]);
    const userId = props.isOwnProfileView ? 'me' : apiUser.id;

    const isNotViewMode = props.mode !== EditFrameMode.View;
    const binder = useGuiBinder<ApiUser>({
        ...props,
        canEdit: isNotViewMode,
    });

    const { mutateAsync: activateUser, isPending: activateUserIsPending } = useActivateUser();
    const { blockUserHandler, blockUserIsPending } = useBlockUser(userId, props.refetchData);
    const { unblockUserHandler, unblockUserIsPending } = useUnblockUser(userId, props.refetchData);

    const requestUpdateState = props.requestUpdateState;
    const activateUserHandler = useCallback(
        () => requestUpdateState((latest) => activateUser(latest.id)),
        [requestUpdateState, activateUser],
    );

    const canActivate = hasPermission(Permission.PermissionsUsersActivate);
    const canResetPassword = hasPermission(Permission.PermissionsUsersResetPassword);
    const canBlock = hasPermission(Permission.PermissionsUsersBlock);
    const canUnblock = hasPermission(Permission.PermissionsUsersUnblock);

    const blockOrUnblockButton = isUserBlocked
        ? canUnblock && (
              <ButtonWithConfirmationModal
                  key="unblock_user_button"
                  onClick={unblockUserHandler}
                  locked={unblockUserIsPending || isNotViewMode}
                  confirmModalMessage={{
                      message: 'Wollen Sie den Benutzer wirklich entsperren?',
                      title: 'Entsperren bestätigen',
                      type: EnumMessageType.MultipleChoice,
                      level: EnumMessageLevel.Information,
                  }}
                  variant="secondary"
              >
                  {unblockUserIsPending ? 'Benutzer wird entsperrt...' : 'Benutzer entsperren'}
              </ButtonWithConfirmationModal>
          )
        : canBlock && (
              <ButtonWithConfirmationModal
                  key="block_user_button"
                  onClick={blockUserHandler}
                  locked={blockUserIsPending || isNotViewMode}
                  confirmModalMessage={{
                      message: 'Wollen Sie den Benutzer wirklich sperren?',
                      title: 'Sperren bestätigen',
                      type: EnumMessageType.MultipleChoice,
                      level: EnumMessageLevel.Warning,
                  }}
                  variant="secondary"
              >
                  {blockUserIsPending ? 'Benutzer wird gesperrt...' : 'Benutzer sperren'}
              </ButtonWithConfirmationModal>
          );

    const { permissions, refetch, groups } = usePermissionsWithGroups('users', userId);
    const permissionsColumns = useMemo(() => getPermissionsColumns(groups), [groups]);

    const {
        refetchRef: refetchGroupsRef,
        linkListApi: blankLinkGroupApi,
        linkedObjectOverlayEvents: linkGroupOverlayEvents,
    } = useLinkedList<ApiUser, ApiGroup>(
        baseApiObjectPath,
        UserLinkLists.groups,
        isNotViewMode && hasPermission(Permission.PermissionsGroupsCreate),
        FrameEditGroup,
    );

    const linkGroupApiWithPermissionRefetch = useMemo(() => {
        const linkGroupAndRefetchPermissions: typeof blankLinkGroupApi.link = async (linkId: string) => {
            const result = await blankLinkGroupApi.link(linkId);
            refetch();
            return result;
        };
        const unlinkGroupAndRefetchPermissions: typeof blankLinkGroupApi.unlink = async (linkId: string) => {
            const result = await blankLinkGroupApi.unlink(linkId);
            refetch();
            return result;
        };

        return {
            ...blankLinkGroupApi,
            link: linkGroupAndRefetchPermissions,
            unlink: unlinkGroupAndRefetchPermissions,
        };
    }, [blankLinkGroupApi, refetch]);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel(props.isOwnProfileView ? 'Profil' : 'Benutzer', props.mode),
                resourceLabel: 'Benutzer',
                ...props.events,
                onAdd: props.isOwnProfileView ? undefined : props.events.onAdd,
                actionItems: [
                    isUserActivated
                        ? canResetPassword && (
                              <ResetPasswordButton
                                  key="reset_password_button"
                                  userId={userId}
                                  refetchData={props.refetchData}
                                  enabled={!isNotViewMode && !isUserPending}
                                  disabledTooltip={
                                      isUserPending ? 'Der Benutzer hat den Zugang noch nicht bestätigt.' : undefined
                                  }
                              />
                          )
                        : canActivate && (
                              <Button
                                  key="activate_user_button"
                                  onClick={activateUserHandler}
                                  locked={activateUserIsPending || isNotViewMode}
                                  variant="secondary"
                              >
                                  {activateUserIsPending
                                      ? 'Benutzer wird aktiviert...'
                                      : isUserRejected
                                        ? 'Erneut einladen'
                                        : 'Benutzer aktivieren'}
                              </Button>
                          ),
                    isUserActivated && !props.isOwnProfileView && blockOrUnblockButton,
                ],
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="PrimaryData" border={'withoutBorder'} label="Allgemeine Informationen" badges={stateBadges}>
                <Grid columns={12}>
                    <GridColumns spans={[6, 6]}>
                        <TextInput {...binder.bindToProp<string>('first_name')} label={i18n('Vorname')} />
                        <TextInput {...binder.bindToProp<string>('last_name')} label={i18n('Nachname')} />
                    </GridColumns>
                    <GridColumns spans={[6, 6]}>
                        <TextInput {...binder.bindToProp<string>('email')} label={i18n('E-Mail-Adresse')} />
                        {!isNotViewMode && isUserActivated && (
                            <TextInput
                                {...binder.bindToProp<string>('last_login_time')}
                                locked
                                label={i18n('Letzte Anmeldung')}
                            />
                        )}
                    </GridColumns>
                </Grid>
            </Card>

            <Card name="groups" label="Benutzergruppen" maxWidth={'80rem'}>
                <LinkedListWithLayoutSettings<ApiGroup>
                    layoutSettingsContext={EnumFrontendLayoutSetting.GroupsTableLayout}
                    layoutSettingsModalContext={EnumFrontendLayoutSetting.GroupsTableLayout}
                    {...binder.bindToLinkList(linkGroupApiWithPermissionRefetch)}
                    {...linkGroupOverlayEvents}
                    refetchRef={refetchGroupsRef}
                    columns={groupColumns}
                    {...(props.isOwnProfileView && { onUnlink: undefined, onLink: undefined })}
                />
            </Card>
            <Card name="permissions" label="Berechtigungen" maxWidth={'80rem'}>
                <p className={'text-right'}>Berechtigungen werden über Benutzergruppen verwaltet.</p>
                <PermissionsList
                    linkedPermissions={permissions.map((obj) => obj.key)}
                    extraColumns={permissionsColumns}
                />
            </Card>
        </EditFrameShell>
    );
}
